import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { ChevronRight, MessageSquare, Plus } from 'lucide-react';
import { Avatar, Badge, Button, Card, PageLoader, Typography } from '../ui';
import { AvatarPhotoEditor } from '../AvatarPhotoEditor';
import { contactThreadPath, isRecipientId, setContactAvatar, useContactEntryQuery, type ContactId, type WebContact } from '../../../lib/contact-book';
import {
  OUTREACH_SECTIONS,
  canReachOut,
  channelLabelKey,
  contactDisplayName,
  longRelativeTime,
  outreachSections,
  reachOutHandle,
  shortRelativeTime,
  sourceLabelKey,
  threadPreview,
  threadTitle,
} from '../../../lib/contact-rows';
import { ContactReadError } from './ContactReadError';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { chatHandoffLink } from '../../../lib/chat-handoff';
import { useBasePath } from '../../../lib/use-base-path';
import { avatarInitials, avatarSlotFor } from '../../../lib/avatar-color';
import { useAgentName } from '../../../lib/use-agent-name';
import { useNarrow } from '../../../lib/use-narrow';
import { useTranslation } from '../../../lib/i18n';
import { useContactSeen } from '../../../lib/use-contact-seen';
import { AddChannelDialog } from './AddChannelDialog';
import { ChannelChip } from './ChannelChip';
import { ContactOutreachViewer } from './ContactOutreachViewer';
import { TurnBadge } from './TurnBadge';
import styles from './contacts.module.css';

/**
 * One contact — the Outreach detail screen (Figma web `17937:269038`, native `17937:264777`).
 *
 * Desktop is two columns: an identity card (avatar · name · Reach out) on the left, and the details
 * column on the right — Channels, "What The Maestro knows", "Needs you", and the info rows. Mobile-web
 * drops the identity card and centres the avatar/name/Reach out above the same details, matching the
 * native phones. Shared 1:1 with iOS `ContactDetailScreen` and Android `ContactDetailScreen`.
 *
 * Everything on screen arrives in the one contact-book read filtered to this person — no second call.
 */
export function ContactDetailBody({ id }: { id: ContactId }) {
  const { t, i18n } = useTranslation();
  const basePath = useBasePath();
  const narrow = useNarrow();
  const agent = useAgentName();
  const { logPageClickBi } = useGenericBi();
  // The real query signals, not `useContactEntry`'s data-only view: that one reports `undefined` for a
  // FAILED read exactly as for a pending one, so `if (!entry) return <PageLoader />` below painted a
  // spinner that never resolved whenever the contact-book read failed — and did the same for a read that
  // succeeded with no contact in it. The trap is documented on `useContactEntryQuery`; the viewer sheet
  // already read it this way and these two pages did not.
  const { entry, isLoading, isError, refetch } = useContactEntryQuery(id);
  const seen = useContactSeen();
  const seenMark = seen.mark;
  const [imageError, setImageError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  // The person id when this is a person, empty for an address-only contact — the seen-mark and avatar
  // seed use whichever the identity carries, and the person-only edits (photo override, add-channel)
  // are hidden for a recipient (#10660): they write to the People service BY person id.
  const recipient = isRecipientId(id);
  const personId = recipient ? '' : id.personId;
  const seedId = recipient ? id.recipientId : id.personId;
  useEffect(() => {
    if (entry) seenMark(seedId);
  }, [entry, seedId, seenMark]);

  // Every conversation with this person, flat and grouped by whose move it is — NOT the per-channel
  // roll-up this used to be. A channel has no subject and no last message, so the old rows could not
  // say what the ask WAS; and they sat under a "Needs you" heading whether or not anyone was waiting
  // on the user. See `outreachSections`.
  const sections = useMemo(() => outreachSections(entry ?? {}), [entry]);
  // A conversation row opens THAT outreach in the viewer, in place. The header there is a no-op — we
  // are already on the contact screen (`onOpenContact` omitted).
  const [viewerOutreachId, setViewerOutreachId] = useState<string | null>(null);

  // Channels sharing a title (e.g. two "Phone" entries) collapse into ONE card that lists every value,
  // with a single "Preferred" badge if any of them is preferred. Values are deduped so an identical
  // number isn't shown twice.
  const channelGroups = useMemo(() => {
    const order: string[] = [];
    const byLabel = new Map<string, { label: string; values: string[]; preferred: boolean }>();
    for (const channel of entry?.channels ?? []) {
      const label = channel.label ?? '';
      const group = byLabel.get(label);
      if (group) {
        if (channel.value && !group.values.includes(channel.value)) group.values.push(channel.value);
        group.preferred = group.preferred || !!channel.preferred;
      } else {
        byLabel.set(label, { label, values: channel.value ? [channel.value] : [], preferred: !!channel.preferred });
        order.push(label);
      }
    }
    return order.map((label) => byLabel.get(label)!);
  }, [entry]);

  if (isLoading) return <PageLoader />;
  if (!entry) return <ContactReadError canRetry={isError} onRetry={refetch} />;

  const card = entry.contact;
  const displayName = contactDisplayName(card, entry.channels, t('contacts.row.unnamed'));
  // The address rides beside the name (`reachOutHandle`) so the agent resolves the right one of two
  // same-named people without asking.
  const handle = reachOutHandle(displayName, entry.channels);
  const reachOutSeed = handle
    ? t('contacts.detail.reachOutSeedWithHandle', { name: displayName, handle })
    : t('contacts.detail.reachOutSeed', { name: displayName });
  const reachOutHref = chatHandoffLink({ kind: 'new', message: reachOutSeed }, basePath).route;

  // The photo override is a person-only write; an address-only contact shows a plain avatar (#10660).
  const avatar = recipient ? (
    <Avatar initials={avatarInitials(displayName)} photoUrl={card?.effectiveAvatarUrl} size={48} color={avatarSlotFor(seedId || displayName)} />
  ) : (
    <AvatarPhotoEditor
      initials={avatarInitials(displayName)}
      photoUrl={card?.effectiveAvatarUrl}
      size={48}
      color={avatarSlotFor(seedId || displayName)}
      canRemove={!!card?.effectiveAvatarUrl}
      onSave={async (url) => {
        const ok = await setContactAvatar(personId, url);
        if (!ok) throw new Error('contact avatar save failed');
      }}
      onError={setImageError}
      page="contact_detail"
      buttonName="edit_contact_photo"
    />
  );

  // Anybody this screen can show can be reached — including the people in the book nobody has messaged
  // yet, for whom a first outreach is the whole point (`canReachOut`).
  const reachOut = canReachOut(card?.personId, entry.recipientId) ? (
    <Button
      fullWidth
      href={reachOutHref}
      startIcon={<MessageSquare size={16} aria-hidden />}
      onClick={() => logPageClickBi({ page_name: 'contact_detail', button_name: 'reach_out' })}
    >
      {t('contacts.detail.reachOut')}
    </Button>
  ) : (
    <Button fullWidth disabled startIcon={<MessageSquare size={16} aria-hidden />}>
      {t('contacts.detail.reachOut')}
    </Button>
  );

  // Identity: centred avatar/name/button on mobile-web (like native); a left-aligned card on desktop.
  const identity = narrow ? (
    <div className={styles.identityNarrow}>
      {avatar}
      {imageError && <p className={styles.imageError}>{imageError}</p>}
      <Typography variant="h1" color="primary" className={styles.identityNameNarrow}>
        {displayName}
      </Typography>
      {reachOut}
    </div>
  ) : (
    <Card variant="sunken" size="lg" className={styles.identityCard}>
      <div className={styles.identityRow}>
        {avatar}
        <Typography variant="h2" color="primary" className={styles.identityName}>
          {displayName}
        </Typography>
      </div>
      {imageError && <p className={styles.imageError}>{imageError}</p>}
      {reachOut}
    </Card>
  );

  const addedFromKey = sourceLabelKey(entry.source) ? `contacts.detail.addedFrom.${entry.source!.trim().toLowerCase()}` : null;

  const details = (
    <div className={styles.detailSections}>
      {/* Channels */}
      <div className={styles.detailSection}>
        <div className={styles.sectionHead}>
          <Typography variant="h3" color="primary">
            {t('contacts.detail.channels')}
          </Typography>
          {/* Adding a channel is a person-only write — hidden for an address-only contact (#10660). */}
          {!recipient ? (
            <Button
              variant="text"
              size="small"
              startIcon={<Plus size={16} aria-hidden />}
              onClick={() => {
                logPageClickBi({ page_name: 'contact_detail', button_name: 'add_channel' });
                setAddOpen(true);
              }}
            >
              {t('common.add')}
            </Button>
          ) : null}
        </div>
        <div className={styles.channelCards}>
          {channelGroups.map((group) => (
            <Card key={group.label} variant="sunken" size="sm" className={styles.channelCard}>
              {group.preferred && narrow ? (
                <span className={styles.channelBadgeTop}>
                  <Badge variant="agent-profession" size="tiny" label={t('contacts.detail.preferred')} />
                </span>
              ) : null}
              <div className={styles.channelRow}>
                <span className={styles.channelText}>
                  <Typography variant="h4" color="primary">
                    {group.label}
                  </Typography>
                  {group.values.map((value) => (
                    <Typography key={value} variant="p2" color="secondary" className={styles.channelValue}>
                      {value}
                    </Typography>
                  ))}
                </span>
                {group.preferred && !narrow ? (
                  // Top-align the badge once the card has more than two rows (label + 2+ values); a
                  // single-value card keeps it vertically centred, per Figma.
                  <span className={group.values.length > 1 ? styles.channelBadgeRightTop : undefined}>
                    <Badge variant="agent-profession" size="tiny" label={t('contacts.detail.preferred')} />
                  </span>
                ) : null}
              </div>
            </Card>
          ))}
        </div>
        {!recipient ? <AddChannelDialog personId={personId} open={addOpen} onClose={() => setAddOpen(false)} /> : null}
        {viewerOutreachId ? (
          <ContactOutreachViewer
            personId={personId}
            recipientId={recipient ? id.recipientId : undefined}
            outreachId={viewerOutreachId}
            open
            onClose={() => setViewerOutreachId(null)}
          />
        ) : null}
      </div>

      {/* What The Maestro knows */}
      {entry.facts?.length ? (
        <div className={styles.detailSection}>
          <Typography variant="h3" color="primary">
            {t('contacts.detail.knows', { maestroAgentName: agent.name })}
          </Typography>
          <Card variant="sunken" size="lg">
            <ul className={styles.bullets}>
              {entry.facts.map((fact) => (
                <li key={fact} className={styles.bullet}>
                  <span className={styles.bulletDot} aria-hidden />
                  <Typography variant="p2" color="primary">
                    {fact}
                  </Typography>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      ) : null}

      {/* Conversations — one row per OUTREACH, grouped by whose move it is. The heading of each group
          is the same string as the badge its rows wear, so a row can never sit under a heading that
          contradicts it (which "Needs Attention" did, for every thread the user was not waiting on).
          The count is a sibling of the heading rather than part of the string: it kept every one of
          these keys unchanged across 37 locales, and RTL flips the pair for free. */}
      {OUTREACH_SECTIONS.map(({ key, labelKey }) => {
        const rows = sections[key];
        if (!rows.length) return null;
        return (
          <div className={styles.detailSection} key={key}>
            <span className={styles.outreachSectionHead}>
              <Typography variant="h3" color="primary">
                {t(labelKey)}
              </Typography>
              <Typography variant="h3" color="secondary">
                {`\u00b7 ${rows.length}`}
              </Typography>
            </span>
            <Card variant="sunken" size="lg">
              <div className={styles.needsRows}>
                {rows.map((row) => (
                  <Link
                    key={row.outreachId}
                    // `contactThreadPath` is RELATIVE — a raw <Link> must carry the active mount prefix
                    // or the deep link (and a modified/new-tab click) breaks on /orion-web and
                    // /symphony-web. This now navigates on a PLAIN click too: the row is one outreach,
                    // so the per-outreach route already renders exactly it and there is nothing left
                    // for an in-place viewer to stitch together.
                    href={`${basePath}${contactThreadPath(id, row.outreachId)}`}
                    className={styles.needsRow}
                    onClick={(e) => {
                      // Plain click opens the viewer in place; a modified click (new tab / cmd) still
                      // resolves the deep-link route, which renders the same conversation full-page.
                      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
                      e.preventDefault();
                      logPageClickBi({ page_name: 'contact_detail', button_name: 'open_outreach' });
                      setViewerOutreachId(row.outreachId);
                    }}
                  >
                    <span className={styles.outreachRowMain}>
                      <Typography variant="h4" color="primary">
                        {threadTitle(row, i18n.language)}
                      </Typography>
                      <Typography variant="p3" color="secondary" className={styles.outreachRowPreview}>
                        {threadPreview(row)}
                      </Typography>
                      <ChannelChip channel={row.channel} outlined />
                    </span>
                    <span className={styles.needsEnd}>
                      <TurnBadge turn={row.turn} />
                      <Typography variant="p3" color="secondary">
                        {shortRelativeTime(row.updatedAt ?? row.createdAt, undefined, t('contacts.time.now'))}
                      </Typography>
                      <ChevronRight size={24} className="dir-flip" aria-hidden />
                    </span>
                  </Link>
                ))}
              </div>
            </Card>
          </div>
        );
      })}

      {/* Info rows */}
      <div className={styles.infoRows}>
        {entry.source ? (
          <Card variant="sunken" size="sm" className={styles.infoCard}>
            <Typography variant="h4" color="primary">
              {addedFromKey ? t(addedFromKey) : t('contacts.detail.addedFrom.other', { source: entry.source })}
            </Typography>
          </Card>
        ) : null}
        {card?.lastTouchAt ? (
          <Card variant="sunken" size="sm" className={styles.infoCard}>
            <Typography variant="h4" color="primary">
              {t('contacts.detail.lastContacted')}
            </Typography>
            <Typography variant="p2" color="secondary">
              {longRelativeTime(card.lastTouchAt)}
            </Typography>
          </Card>
        ) : null}
      </div>
    </div>
  );

  return narrow ? (
    <div className={styles.detailRoot}>
      {identity}
      {details}
    </div>
  ) : (
    <div className={styles.detailTwoCol}>
      <div className={styles.identityCol}>{identity}</div>
      <div className={styles.detailsCol}>{details}</div>
    </div>
  );
}

export type { WebContact };
